'use client';
import { useState, useMemo, useEffect } from 'react';
import dayjs from 'dayjs';
import { Box, Stack, Card, Typography, Divider, Button, Chip } from '@mui/material';
import ArrowRightAltIcon from '@mui/icons-material/ArrowRightAlt';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import RouteIcon from '@mui/icons-material/Route';
import CloseIcon from '@mui/icons-material/Close';
import ListAltIcon from '@mui/icons-material/ListAlt';
import { Tabs, Tab, Tooltip } from '@mui/material';
import InformationBubbleDialog from '../../../components/InformationBubbleDialog';
import { API_BASES } from '../../../config/constants';
import { timezone } from '../../../utils/timeZone';

const formatUI_DateTime = (input) => {
  try {
    if (!input) return '-';
    const d = new Date(input);
    if (isNaN(d.getTime())) return String(input);
    const dd = String(d.getDate()).padStart(2, '0');
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const yy = String(d.getFullYear()).slice(-2);
    const hh = String(d.getHours()).padStart(2, '0');
    const min = String(d.getMinutes()).padStart(2, '0');
    return `${dd}/${mm}/${yy} ${hh}:${min}`;
  } catch {
    return String(input ?? '-');
  }
};

export default function TripHistoryListForShare({
  tripList = [],
  activePlayId,
  onTogglePlay,
  fleetName = 'Vehicle',
  startTime,
  endTime,
  token,
  companyId
}) {
  const [expanded, setExpanded] = useState({});
  const [detailsExpanded, setDetailsExpanded] = useState({});
  const [activeTab, setActiveTab] = useState('All');

  const toggleExpand = (id) => setExpanded((p) => ({ ...p, [id]: !p[id] }));
  const toggleDetails = (id) => setDetailsExpanded((p) => ({ ...p, [id]: !p[id] }));

  const [allRoutes, setAllRoutes] = useState([]);

  useEffect(() => {
    const fetchRoutes = async () => {
      try {
        if (!token) return;

        // Build payload matching useRouteTripAll
        // paging: start 0, length 10000, join company (name), order id desc
        const payload = {
          paging: { start: 0, length: 10000 },
          columns: [],
          order: [{ column: 'id', dir: 'desc' }],
          joins: [{ table: 'company', columns: ['name'] }],
          filter: { condition: 'AND', rules: [] }
        };

        if (companyId) {
          const idStr = String(companyId);
          if (idStr.includes(',')) {
            payload.filter.rules.push({ field: 'company_id', operator: 'in', value: idStr });
          } else {
            payload.filter.rules.push({ field: 'company_id', operator: '=', value: idStr });
          }
        }

        const tzVal = typeof timezone === 'function' ? timezone() : Number(timezone) || 7;
        const res = await fetch(`${API_BASES.FLEET_API}/route-trip/search?tz=${tzVal}&withDetail=true`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json, text/plain, */*',
            Authorization: `Bearer ${token}`
          },
          body: JSON.stringify(payload)
        });

        if (res.ok) {
          const json = await res.json();
          setAllRoutes(json?.data || []);
        }
      } catch (err) {
        console.error('Failed to fetch routes in TripHistoryListForShare', err);
      }
    };

    fetchRoutes();
  }, [token, companyId]);

  const enrichedTripList = useMemo(() => {
    return (tripList || []).map((trip) => {
      const matchedRoute = allRoutes.find((r) => String(r.id) === String(trip.route_trip_id));
      const isActive = matchedRoute ? matchedRoute.is_active === true || matchedRoute.is_active === 'true' : false;
      return { ...trip, isActive };
    });
  }, [tripList, allRoutes]);

  const filteredTripList = useMemo(() => {
    return enrichedTripList.filter((trip) => {
      if (activeTab === 'Active Route') return trip.isActive;
      if (activeTab === 'Historical Route') return !trip.isActive;
      return true;
    });
  }, [enrichedTripList, activeTab]);

  const activeTripsCount = enrichedTripList.filter((t) => t.isActive).length;
  const nonActiveTripsCount = enrichedTripList.filter((t) => !t.isActive).length;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100vh', bgcolor: '#fff' }}>
      {/* HEADER (STICKY) */}
      <Box sx={{ p: 1.5, borderBottom: '1px solid #e2e8f0', flexShrink: 0 }}>
        <Typography variant="subtitle1" fontWeight="bold" sx={{ color: '#1e293b', lineHeight: 1.2 }}>
          Trip History List
        </Typography>
        <Typography variant="caption" color="text.secondary" noWrap sx={{ display: 'block', mb: 1 }}>
          {fleetName}
        </Typography>

        {startTime && endTime && (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              mt: 1,
              mb: 1,
              p: 1,
              bgcolor: '#f8fafc',
              borderRadius: 2,
              border: '1px solid #e2e8f0'
            }}
          >
            <Box sx={{ textAlign: 'left', flex: 1 }}>
              <Typography variant="body2" sx={{ fontWeight: 600, color: '#1e293b', lineHeight: 1.2 }}>
                {dayjs(startTime).format('DD MMM YYYY')}
              </Typography>
              <Typography variant="caption" sx={{ color: '#2c3440ff' }}>
                {dayjs(startTime).format('HH:mm:ss')}
              </Typography>
            </Box>
            <Box sx={{ px: 1, display: 'flex', alignItems: 'center', color: '#94a3b8' }}>
              <ArrowRightAltIcon fontSize="small" />
            </Box>
            <Box sx={{ textAlign: 'right', flex: 1 }}>
              <Typography variant="body2" sx={{ fontWeight: 600, color: '#1e293b', lineHeight: 1.2 }}>
                {dayjs(endTime).format('DD MMM YYYY')}
              </Typography>
              <Typography variant="caption" sx={{ color: '#2c3440ff' }}>
                {dayjs(endTime).format('HH:mm:ss')}
              </Typography>
            </Box>
          </Box>
        )}

        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="caption" sx={{ fontWeight: 600, color: '#485D9A' }}>
            Total Ritase: {filteredTripList.length}
          </Typography>
          <InformationBubbleDialog
            iconSize="small"
            content="Ritase is detected based on currently active routes and non-active routes. Non-active routes are kept for historical purposes because when there is a route change in the past, the route before that time is still saved."
          />
        </Box>

        <Tabs
          value={activeTab}
          onChange={(e, v) => setActiveTab(v)}
          variant="fullWidth"
          sx={{
            minHeight: 28,
            mt: 1,
            bgcolor: '#f1f5f9',
            borderRadius: '6px',
            p: 0.5,
            '& .MuiTabs-indicator': { display: 'none' },
            '& .MuiTab-root': {
              minHeight: 24,
              py: 0,
              px: 0.5,
              borderRadius: '4px',
              textTransform: 'none',
              fontWeight: 600,
              fontSize: '0.65rem',
              color: '#64748b',
              transition: 'all 0.2s',
              '&.Mui-selected': {
                bgcolor: '#ffffff',
                color: '#1e3a8a',
                boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
              }
            }
          }}
        >
          <Tab label="All" value="All" />
          <Tab label="Active Route" value="Active Route" />
          <Tab label="History" value="Historical Route" />
        </Tabs>
      </Box>

      {/* BODY / LIST PERJALANAN (SCROLLABLE) */}
      <Box sx={{ flex: 1, overflowY: 'auto', p: 1.5, scrollbarWidth: 'thin' }}>
        {!filteredTripList || filteredTripList.length === 0 ? (
          <Box sx={{ py: 4, textAlign: 'center', color: 'text.secondary', fontSize: '0.85rem' }}>Tidak ada data perjalanan ritase.</Box>
        ) : (
          filteredTripList.map((t, index) => {
            const tripId = `trip-${index}`;
            const expandedState = !!expanded[tripId];
            const isDetailsVisible = !!detailsExpanded[tripId];
            const playing = activePlayId === tripId;
            const isRouteActive = t.isActive;
            const iconBg = isRouteActive ? (playing ? '#f44336' : '#485D9A') : playing ? '#f44336' : '#9E9E9E';

            return (
              <Card
                key={tripId}
                variant="outlined"
                sx={{ borderRadius: 2, border: playing ? '2px solid #485D9A' : '1px solid #e0e0e0', mb: 1, p: 1 }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Tooltip title={isRouteActive ? 'Active Route' : 'Historical Route'} placement="top">
                    <Box
                      onClick={() => onTogglePlay && onTogglePlay(t, index)}
                      sx={{
                        background: iconBg,
                        borderRadius: 16,
                        width: 34,
                        height: 34,
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        cursor: 'pointer',
                        color: '#fff',
                        flexShrink: 0
                      }}
                    >
                      {playing ? <CloseIcon /> : <RouteIcon />}
                    </Box>
                  </Tooltip>
                  <Box sx={{ flex: 1, overflow: 'hidden' }}>
                    <Stack direction="row" spacing={1}>
                      <Box>
                        <Typography variant="caption" color="text.secondary">
                          Start
                        </Typography>
                        <Typography variant="body2" sx={{ fontWeight: 700, fontSize: '0.7rem' }}>
                          {formatUI_DateTime(t.start_time)}
                        </Typography>
                      </Box>
                      <Box>
                        <Typography variant="caption" color="text.secondary">
                          End
                        </Typography>
                        <Typography variant="body2" sx={{ fontWeight: 700, fontSize: '0.7rem' }}>
                          {formatUI_DateTime(t.end_time)}
                        </Typography>
                      </Box>
                    </Stack>
                  </Box>
                  <Box sx={{ cursor: 'pointer', flexShrink: 0 }} onClick={() => toggleExpand(tripId)}>
                    {expandedState ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                  </Box>
                </Box>

                {/* EXPANDED DETAILS */}
                {expandedState && (
                  <Box sx={{ mt: 1 }}>
                    <Divider sx={{ my: 1 }} />
                    <Stack spacing={1}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Typography variant="caption">Driver / Plate</Typography>
                        <Typography variant="caption" sx={{ fontWeight: 700 }}>
                          {t.license_plate || fleetName || '-'}
                        </Typography>
                      </Box>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Typography variant="caption">Duration</Typography>
                        <Typography variant="caption" sx={{ fontWeight: 700 }}>
                          {t.duration ? `${(t.duration / 60).toFixed(0)} Min` : '-'}
                        </Typography>
                      </Box>

                      <Button
                        size="small"
                        startIcon={<ListAltIcon />}
                        variant={isDetailsVisible ? 'contained' : 'outlined'}
                        sx={{ textTransform: 'none', mt: 0.5, py: 0.2, fontSize: '0.7rem' }}
                        onClick={() => toggleDetails(tripId)}
                      >
                        {isDetailsVisible ? 'Hide Ritase Details' : 'Show Ritase Details'}
                      </Button>

                      {isDetailsVisible && (
                        <Box sx={{ mt: 1, p: 1, bgcolor: '#f8f9fa', borderRadius: 2, border: '1px solid #eeeeee' }}>
                          <Typography variant="caption" fontWeight="bold" color="primary" sx={{ display: 'block', mb: 1 }}>
                            Route Checkpoints:
                          </Typography>

                          {t.routes && t.routes.length > 0 ? (
                            <Stack spacing={1.5}>
                              {t.routes.map((route, rIdx) => (
                                <Box key={rIdx} sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
                                  <Box
                                    sx={{
                                      width: 20,
                                      height: 20,
                                      borderRadius: '50%',
                                      bgcolor: '#485D9A',
                                      color: 'white',
                                      display: 'flex',
                                      alignItems: 'center',
                                      justifyContent: 'center',
                                      fontSize: '0.65rem',
                                      fontWeight: 'bold',
                                      flexShrink: 0
                                    }}
                                  >
                                    {route.sequence_number}
                                  </Box>
                                  <Box>
                                    <Typography variant="body2" fontWeight={600} sx={{ lineHeight: 1.2, fontSize: '0.75rem' }}>
                                      {route.spatial_name}
                                    </Typography>
                                    <Chip label={route.spatial_type} size="small" sx={{ height: 16, fontSize: '0.55rem', mt: 0.25 }} />
                                  </Box>
                                </Box>
                              ))}
                            </Stack>
                          ) : (
                            <Typography variant="caption" color="text.secondary">
                              Tidak ada data rute.
                            </Typography>
                          )}
                        </Box>
                      )}
                    </Stack>
                  </Box>
                )}
              </Card>
            );
          })
        )}
      </Box>

      {/* STICKY BOTTOM SUMMARY */}
      {tripList && tripList.length > 0 && (
        <Box
          sx={{
            bgcolor: '#ffffff',
            borderTop: '1px solid #e2e8f0',
            pt: 1,
            pb: 1.5,
            px: 2,
            display: 'flex',
            justifyContent: 'center',
            gap: 3,
            flexShrink: 0,
            zIndex: 10
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
            <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: '#485D9A', boxShadow: '0 0 0 2px rgba(72, 93, 154, 0.2)' }} />
            <Typography variant="caption" sx={{ fontWeight: 700, color: '#475569', fontSize: '0.65rem' }}>
              {activeTripsCount} Active
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
            <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: '#9E9E9E', boxShadow: '0 0 0 2px rgba(158, 158, 158, 0.2)' }} />
            <Typography variant="caption" sx={{ fontWeight: 700, color: '#475569', fontSize: '0.65rem' }}>
              {nonActiveTripsCount} Historical
            </Typography>
          </Box>
        </Box>
      )}
    </Box>
  );
}
